import React from 'react';
import { ArrowRightLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface Business {
  id: string;
  name: string;
  company_logo?: string | null;
  city?: string;
  postal_code?: string;
}

interface Discount {
  id: string;
  amount: number;
  isPercentage: boolean;
  appliesToAll: boolean;
  type?: 'Paid' | 'Free';
  description?: string | null;
  coupon_code?: string | null;
}

interface RequestDiscountButtonProps {
  business: Business;
  discount: Discount;
  onRequest: () => void;
  className?: string;
}

export const RequestDiscountButton: React.FC<RequestDiscountButtonProps> = ({
  business,
  discount,
  onRequest,
  className
}) => {
  const { t } = useTranslation();
  
  // Always show the request button when discount exists
  // The shouldDisplayDiscountCode parameter only affects coupon code reveal functionality

  // Format the button text with bullet separator for clarity
  const buttonText = discount.isPercentage 
    ? t('service.details.request_quote_percentage', { amount: discount.amount })
    : t('service.details.request_quote_fixed', { amount: discount.amount });

  // Option 1: Original Reveal Code Style (subtle orange background) - SELECTED
  return (
    <div className="w-full">
      {/* Mobile: Full-width button with Original Reveal Style */}
      <button
        onClick={onRequest}
        className={cn(
          "w-full flex items-center justify-between sm:hidden",
          "px-4 py-3",
          "bg-orange-50 hover:bg-orange-100 active:bg-orange-200",
          "text-orange-800 rounded-lg",
          "transition-colors group",
          className
        )}
      >
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4" />
          <span className="text-sm font-semibold">{buttonText}</span>
        </div>
        <ChevronRight className="h-4 w-4 opacity-60 group-hover:translate-x-0.5 transition-transform" />
      </button>
      
      {/* Desktop: Compact button with Original Reveal Style */}
      <Button
        onClick={onRequest}
        className={cn(
          "hidden sm:flex bg-orange-50 hover:bg-orange-100",
          "text-orange-800 font-semibold rounded-md",
          "text-sm h-9 px-4 items-center gap-2 transition-colors"
        )}
      >
        <ArrowRightLeft className="h-4 w-4" />
        <span>{buttonText}</span>
      </Button>
    </div>
  );
};

export default RequestDiscountButton;